import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { AlertCircle, CheckCircle, Loader2, Info, Save, Check } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Notification } from '../../../../../components/ui/custom-notification';
import { Signature } from '../../../../../features/project/components/dashboard/production/tabs/Signature';
import { useDispatch, useSelector } from 'react-redux';
import { useTeamMembers } from '../../../../../redux/ProductionData/hooks/useTeamMembers';
import { useReportDraft } from '../../../../../redux/ReportDrafts/hooks/useReportDraft';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { generateMaterialRegistryReport, clearMaterialRegistryReport, getRegistryReportVersions, getInspectionPersonnelDocuments } from '../../../../../redux/Project/materialsData/action';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';

interface RegistryItem {
  tags_id: number;
  tags_name: string;
  tags_type: string;
}

interface EquipmentSignatureReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  registryItems?: RegistryItem[];
  registryType:
    | 'Equipment'
    | 'Consumable'
    | 'QC Inspection Personnel'
    | 'Welder Certification & Qualification'
    | 'Miscellaneous';
  activeTabName?: string;
  projectId?: number;
  projectName?: string;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  canSign?: boolean;
  canEdit?: boolean;
  isAdminOrSuperAdmin?: boolean;
  onRefresh?: () => void;
}

// Helper function to get the correct report type based on registry type and tab name
const normalizeTabName = (tabName?: string): string =>
  (tabName || '').toLowerCase().replace(/\s+/g, '-');

const getReportTypeForTab = (registryType: string, tabName: string): string => {
  const normalizedTab = normalizeTabName(tabName);
  
  if (registryType === 'Equipment') {
    if (normalizedTab === 'coating') {
      return REPORT_TYPES.EQUIPMENT_COATING_REPORT;
    }
    switch (normalizedTab) {
      case 'welding':
        return REPORT_TYPES.EQUIPMENT_WELDING_REPORT;
      case 'nde':
        return REPORT_TYPES.EQUIPMENT_NDE_REPORT;
      case 'qc':
        return REPORT_TYPES.EQUIPMENT_QC_REPORT;
     
      default:
        return REPORT_TYPES.EQUIPMENT_WELDING_REPORT;
    }
  } else if (registryType === 'Consumable') {
    if (normalizedTab === 'coating') {
      return REPORT_TYPES.CONSUMABLE_COATING_REPORT;
    }
    switch (normalizedTab) {
      case 'wires':
      case 'wire':
        return REPORT_TYPES.CONSUMABLE_WIRE_REPORT;
      case 'gas':
        return REPORT_TYPES.CONSUMABLE_GAS_REPORT;
      case 'nde':
        return REPORT_TYPES.CONSUMABLE_NDE_REPORT;
      default:
        return REPORT_TYPES.CONSUMABLE_NDE_REPORT;
    }
  }
  
  return REPORT_TYPES.EQUIPMENT_WELDING_REPORT;
};

/** Draft fetch/save reportType: per equipment tab (welding/nde/qc/coating); per consumable tab (nde/coating). */
const getDraftReportType = (registryType: string, activeTabName?: string): string => {
  if (registryType === 'Consumable') {
    const normalizedTab = normalizeTabName(activeTabName);
    if (normalizedTab === 'coating') {
      return REPORT_TYPES.CONSUMABLE_COATING_REPORT;
    }
    if (normalizedTab === 'nde') {
      return REPORT_TYPES.CONSUMABLE_NDE_REPORT;
    }
    return getReportTypeForTab('Consumable', activeTabName || 'nde');
  }
  if (registryType === 'QC Inspection Personnel') return REPORT_TYPES.QC_INSPECTION_PERSONNEL_REPORT;
  if (registryType === 'Welder Certification & Qualification')
    return REPORT_TYPES.WELDER_CERTIFICATION_QUALIFICATION_REPORT;
  if (registryType === 'Miscellaneous') return REPORT_TYPES.MATERIAL_REGISTRY_MISCELLANEOUS;
  if (registryType === 'Equipment') {
    return getReportTypeForTab('Equipment', activeTabName || 'welding');
  }
  return REPORT_TYPES.EQUIPMENT_WELDING_REPORT;
};

/** Prefer the registry tab the user opened from (e.g. coating) over stale draft selectedTabs. */
const resolveTabsForActiveContext = (
  registryType: string,
  activeTabName: string | undefined,
  registryItems: RegistryItem[],
  selectedTabs: string[],
): string[] => {
  if (
    (registryType !== 'Consumable' && registryType !== 'Equipment') ||
    !activeTabName ||
    registryItems.length === 0
  ) {
    return selectedTabs;
  }

  const activeNorm = normalizeTabName(activeTabName);
  const matchingItem = registryItems.find(
    (item) => normalizeTabName(item.tags_name) === activeNorm,
  );
  if (matchingItem) {
    return [matchingItem.tags_name];
  }

  if (selectedTabs.length > 0) {
    return selectedTabs;
  }

  return registryItems.map((item) => item.tags_name);
};

const getConsumableReportTitle = (activeTabName?: string, selectedTabs: string[] = []): string => {
  const tabHint = normalizeTabName(activeTabName) || normalizeTabName(selectedTabs[0]);
  if (tabHint === 'coating') {
    return 'Consumable Coating Registry Report';
  }
  if (tabHint === 'nde') {
    return 'Consumable NDE Registry Report';
  }
  return 'Consumable Registry Report';
};

export const EquipmentSignatureReportModal: React.FC<EquipmentSignatureReportModalProps> = ({
  isOpen,
  onClose,
  registryItems = [],
  registryType,
  activeTabName,
  projectId,
  projectName,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  canSign = true,
  canEdit = true,
  isAdminOrSuperAdmin = true,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const { teamMembers, loadTeamMembers } = useTeamMembers();

  // Redux state for report generation and versions
  const {
    materialRegistryReportLoading,
    materialRegistryReportSuccess,
    materialRegistryReportError,
    materialRegistryReportData,
    registryReportVersionsLoading,
    registryReportVersions,
    registryReportVersionsError,
    inspectionPersonnelDocumentsLoading,
    inspectionPersonnelDocuments,
    inspectionPersonnelDocumentsError
  } = useSelector((state: any) => state.materialsData || {});

  // Draft management
  const {
    draft,
    draftSignatures,
    isFetchingDraft,
    isSavingDraft,
    saveDraftSuccess,
    saveDraftError,
    saveDraft,
    loadDraft,
    clearDraft,
    clearSaveStatus,
    draftId
  } = useReportDraft();

  // Selected tabs state (checkboxes for Welding, NDE, QC)
  const [selectedTabs, setSelectedTabs] = useState<string[]>([]);

  // Form fields for report generation
  const [procedureDocumentNo, setProcedureDocumentNo] = useState<string>('');
  const [registerRevision, setRegisterRevision] = useState<string>('0');

  // Version history state - track expanded groups per category
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // PDF Viewer state
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  // Track if form has been modified (dirty state)
  const [isDirty, setIsDirty] = useState(false);

  // Track if draft has been saved at least once (required before generation)
  const [hasSavedDraft, setHasSavedDraft] = useState<boolean>(false);

  // Team member lists
  const [qcList, setQcList] = useState<Array<{ value: string, label: string, name?: string, userId?: string }>>([]);
  const [customerRepresentativeList, setCustomerRepresentativeList] = useState<Array<{ value: string, label: string, name?: string, userId?: string }>>([]);
  const [selectedCustomerRepresentativeId, setSelectedCustomerRepresentativeId] = useState<string | null>(null);
  const [selectedQcInspectorId, setSelectedQcInspectorId] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<any>(null);
  const [isProductionSheetEditMode, setIsProductionSheetEditMode] = useState(true);

  // Signature handlers
  const handleQcSignatureChange = (value: string | null) => {
    setIsDirty(true);
    setSelectedQcInspectorId(value);
  };

  const handleCustomerSignatureChange = (value: string | null) => {
    setIsDirty(true);
    setSelectedCustomerRepresentativeId(value);
  };

  const OnHandleSignature = (data: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    setSignatureData((prev: any) => ({
      ...prev,
      signature: {
        ...prev?.signature,
        [data.type]: {
          signature: data.signature,
          name: data.name,
          comment: data.comment,
          stampImage: data.stampImage,
          timestamp: new Date().toISOString()
        }
      }
    }));
    setIsDirty(true);
  };

  // Handle tab selection toggle
  const handleTabToggle = useCallback((tabName: string) => {
    setSelectedTabs(prev => {
      if (prev.includes(tabName)) {
        return prev.filter(t => t !== tabName);
      } else {
        return [...prev, tabName];
      }
    });
    setIsDirty(true);
  }, []);

  // Handle select all tabs - show all consumable types (NDE, Coating, etc.)
  const handleSelectAllTabs = useCallback(() => {
    const currentItems = registryItems;
    if (selectedTabs.length === currentItems.length) {
      setSelectedTabs([]);
    } else {
      setSelectedTabs(currentItems.map(item => item.tags_name));
    }
    setIsDirty(true);
  }, [selectedTabs.length, registryItems]);

  // Load team members when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      if (!teamMembers || teamMembers.length === 0) {
        loadTeamMembers(projectId);
      } else {
        loadAllTeamMembers();
      }
    }
  }, [isOpen, projectId, teamMembers, loadTeamMembers]);

  // Clear global draft then fetch for this report type (layout phase so stale Coating data cannot apply before Welding fetch starts)
  useLayoutEffect(() => {
    if (isOpen && projectId) {
      clearDraft();
      loadDraft({
        projectId,
        reportType: getDraftReportType(registryType, activeTabName),
      });
    }
  }, [isOpen, projectId, registryType, activeTabName, loadDraft, clearDraft]);

  // After fetch: no server draft — reset local form (runs before auto-select so selectedTabs can repopulate)
  useEffect(() => {
    if (!isOpen || isFetchingDraft || draft != null) {
      return;
    }
    setHasSavedDraft(false);
    setProcedureDocumentNo('');
    setRegisterRevision('0');
    setSelectedCustomerRepresentativeId(null);
    setSelectedQcInspectorId(null);
    setSignatureData(null);
    setSelectedTabs([]);
    setIsDirty(false);
  }, [isOpen, isFetchingDraft, draft]);

  // Auto-select consumable tab(s) when modal opens - use activeTabName so Coating/NDE tab is pre-selected
  useEffect(() => {
    if (isOpen && registryType === 'Consumable' && registryItems.length > 0 && !isFetchingDraft) {
      const shouldAutoSelect = (!draft || !draft.selectedTabs || !Array.isArray(draft.selectedTabs) || draft.selectedTabs.length === 0)
        && selectedTabs.length === 0;

      if (shouldAutoSelect) {
        const resolvedTabs = resolveTabsForActiveContext(
          registryType,
          activeTabName,
          registryItems,
          [],
        );
        if (resolvedTabs.length > 0) {
          setSelectedTabs(resolvedTabs);
        }
      }
    }
  }, [isOpen, registryType, registryItems, draft, isFetchingDraft, selectedTabs.length, activeTabName]);

  // Auto-select equipment tab from activeTabName (e.g. Coating report button) when draft has no tabs yet
  useEffect(() => {
    if (isOpen && registryType === 'Equipment' && registryItems.length > 0 && !isFetchingDraft) {
      const shouldAutoSelect =
        (!draft ||
          !draft.selectedTabs ||
          !Array.isArray(draft.selectedTabs) ||
          draft.selectedTabs.length === 0) &&
        selectedTabs.length === 0;

      if (shouldAutoSelect) {
        const resolvedTabs = resolveTabsForActiveContext(
          registryType,
          activeTabName,
          registryItems,
          [],
        );
        if (resolvedTabs.length > 0) {
          setSelectedTabs(resolvedTabs);
        }
      }
    }
  }, [
    isOpen,
    registryType,
    registryItems,
    draft,
    isFetchingDraft,
    selectedTabs.length,
    activeTabName,
  ]);

  // Reload draft after successful save to get fresh state
  useEffect(() => {
    if (saveDraftSuccess && projectId) {
      setHasSavedDraft(true);
      setIsDirty(false);
      clearDraft();
      loadDraft({
        projectId,
        reportType: getDraftReportType(registryType, activeTabName),
      });
    }
  }, [saveDraftSuccess, projectId, registryType, activeTabName, loadDraft, clearDraft]);

  // Auto-open PDF viewer when report generates successfully (only if modal is open)
  useEffect(() => {
    if (isOpen && materialRegistryReportSuccess && materialRegistryReportData) {
      const fileUrl = materialRegistryReportData?.downloadUrl || materialRegistryReportData?.data?.downloadUrl;
      const fileName = materialRegistryReportData?.fileName || materialRegistryReportData?.data?.fileName || `${registryType} Registry Report`;
      if (fileUrl) {
        setViewerFile({ fileUrl, fileName });
      }
    }
  }, [isOpen, materialRegistryReportSuccess, materialRegistryReportData, registryType]);

  // Apply draft data when loaded
  useEffect(() => {
    if (!isOpen || !draft || isFetchingDraft) {
      return;
    }

    setHasSavedDraft(true);

    const draftTabs = draft.selectedTabs && Array.isArray(draft.selectedTabs) ? draft.selectedTabs : [];
    const resolvedTabs = resolveTabsForActiveContext(
      registryType,
      activeTabName,
      registryItems,
      draftTabs,
    );
    if (resolvedTabs.length > 0) {
      setSelectedTabs(resolvedTabs);
    }

    if (draft.procedureDocumentNo) {
      setProcedureDocumentNo(draft.procedureDocumentNo);
    }
    if (draft.registerRevision) {
      setRegisterRevision(draft.registerRevision);
    }

    if (draft.selectedQcInspectorId && qcList.length > 0) {
      const matchingQc = qcList.find(m => m.value === draft.selectedQcInspectorId);
      if (matchingQc) {
        setSelectedQcInspectorId(matchingQc.value);
      }
    }

    if (draft.selectedCustomerRepresentativeId && customerRepresentativeList.length > 0) {
      const matchingCustomer = customerRepresentativeList.find(m => m.value === draft.selectedCustomerRepresentativeId);
      if (matchingCustomer) {
        setSelectedCustomerRepresentativeId(matchingCustomer.value);
      }
    }

    if (draftSignatures) {
      if (draftSignatures.qc && draftSignatures.qc.signed) {
        setSignatureData((prev: any) => ({
          ...prev,
          signature: {
            ...prev?.signature,
            qc: {
              signature: draftSignatures.qc.signature || '',
              name: draftSignatures.qc.name || '',
              comment: draftSignatures.qc.comment || '',
              stampImage: draftSignatures.qc.stampImage || '',
              timestamp: draftSignatures.qc.timestamp || ''
            }
          }
        }));
      }

      if (draftSignatures.customer && draftSignatures.customer.signed) {
        setSignatureData((prev: any) => ({
          ...prev,
          signature: {
            ...prev?.signature,
            customer: {
              signature: draftSignatures.customer.signature || '',
              name: draftSignatures.customer.name || '',
              comment: draftSignatures.customer.comment || '',
              stampImage: draftSignatures.customer.stampImage || '',
              timestamp: draftSignatures.customer.timestamp || ''
            }
          }
        }));
      }
    }

    setIsDirty(false);
  }, [isOpen, draft, draftSignatures, isFetchingDraft, qcList, customerRepresentativeList, registryType, activeTabName, registryItems]);

  // Restore selected users from draft signatures after team member lists are loaded
  useEffect(() => {
    if (!isOpen || !draftSignatures || qcList.length === 0 || customerRepresentativeList.length === 0) {
      return;
    }

    if (draftSignatures.qc && draftSignatures.qc.signed && draftSignatures.qc.name) {
      const matchingQc = qcList.find(m =>
        m.name === draftSignatures.qc.name ||
        m.label === draftSignatures.qc.name ||
        m.label?.includes(draftSignatures.qc.name) ||
        m.name?.includes(draftSignatures.qc.name)
      );
      if (matchingQc && !selectedQcInspectorId) {
        setSelectedQcInspectorId(matchingQc.value);
      }
    }

    if (draftSignatures.customer && draftSignatures.customer.signed && draftSignatures.customer.name) {
      const matchingCustomer = customerRepresentativeList.find(m =>
        m.name === draftSignatures.customer.name ||
        m.label === draftSignatures.customer.name ||
        m.label?.includes(draftSignatures.customer.name) ||
        m.name?.includes(draftSignatures.customer.name)
      );
      if (matchingCustomer && !selectedCustomerRepresentativeId) {
        setSelectedCustomerRepresentativeId(matchingCustomer.value);
      }
    }
  }, [isOpen, draftSignatures, qcList, customerRepresentativeList, selectedQcInspectorId, selectedCustomerRepresentativeId]);

  // Load team members when they change
  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) 
    {
      loadAllTeamMembers();
    }
  }, [teamMembers]);

  useEffect(() => {
    if (isOpen && projectId) {
      {
        loadTeamMembers(projectId);
      }
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (
      isOpen &&
      projectId &&
      (registryType === 'QC Inspection Personnel' ||
        registryType === 'Welder Certification & Qualification')
    ) {
      dispatch(getInspectionPersonnelDocuments(projectId));
    }
  }, [isOpen, projectId, registryType, dispatch]);


  const loadAllTeamMembers = async () => {
    if (teamMembers) {
      const qcInspectors = teamMembers
        .filter((member: any) => member?.team_member_role === 'Quality Control' || member?.team_member_role === 'QC' || member?.team_member_role === 'QA/QC')
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));

      const customerReps = teamMembers
        .filter((member: any) => member?.team_member_role === 'Customer Representative' || member?.team_member_role === 'Client Representative')
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));

      if (isAdminOrSuperAdmin) {
        setQcList(qcInspectors);
      }
      else if (isQCInspectorUser && qcInspectors.length > 0) {
        setQcList(qcInspectors.filter((member: any) => member.userId === currentUserTeamMemberId));
      }
      else {
        setQcList([]);
      }

      if (isAdminOrSuperAdmin) {
        setCustomerRepresentativeList(customerReps);
      }
      else if (isCustomerRepresentativeUser && customerReps.length > 0) {
        setCustomerRepresentativeList(customerReps.filter((member: any) => member.userId === currentUserTeamMemberId));
      }
      else {
        setCustomerRepresentativeList([]);
      }
    }
  };

  // Check if both signatures are provided
  const isBothSignaturesProvided = useMemo(() => {
    if (!signatureData?.signature) return false;
    const currentSignature = signatureData.signature;
    return !!(currentSignature.qc && currentSignature.customer);
  }, [signatureData]);

  // Check if form is valid for draft saving
  const isDraftValid = useMemo(() => {
    // QC, Welder, Consumable, Miscellaneous don't need selectedTabs
    if (
      registryType === 'QC Inspection Personnel' ||
      registryType === 'Welder Certification & Qualification' ||
      registryType === 'Consumable' ||
      registryType === 'Miscellaneous'
    )
      return true;
    return selectedTabs.length > 0;
  }, [selectedTabs, registryType]);

  // Check if form is valid for report generation
  const isFormValid = useMemo(() => {
    // QC & Welder registers: register revision + signatures (same as QC flow)
    if (
      registryType === 'QC Inspection Personnel' ||
      registryType === 'Welder Certification & Qualification' ||
      registryType === 'Miscellaneous'
    ) {
      return isBothSignaturesProvided && registerRevision.trim().length > 0;
    }
    // Consumable: requires both signatures and procedureDocumentNo (selectedTabs auto-populated with NDE)
    if (registryType === 'Consumable') {
      return isBothSignaturesProvided && procedureDocumentNo.trim().length > 0;
    }
    // Equipment: requires selectedTabs, both signatures, and procedureDocumentNo
    return selectedTabs.length > 0 && isBothSignaturesProvided && procedureDocumentNo.trim().length > 0;
  }, [selectedTabs, isBothSignaturesProvided, procedureDocumentNo, registerRevision, registryType]);

  // Check if report can be generated: form valid + draft saved + no pending changes
  const canGenerate = useMemo(() => {
    return isFormValid && hasSavedDraft && !isDirty;
  }, [isFormValid, hasSavedDraft, isDirty]);

  // Get tooltip message for generate button based on current state
  const generateButtonTooltip = useMemo(() => {
    if (!isFormValid) {
      return 'Complete all required fields and add both signatures';
    }
    if (!hasSavedDraft) {
      return 'Save draft before generating report';
    }
    if (isDirty) {
      return 'Save your changes before generating report';
    }
    return '';
  }, [isFormValid, hasSavedDraft, isDirty]);

  // Get the report type prefix based on registryType
  const reportTypePrefix = useMemo(() => {
    if (registryType === 'Equipment') return 'EQUIPMENT_';
    if (registryType === 'Consumable') return 'CONSUMABLE_';
    if (registryType === 'QC Inspection Personnel') return 'QC_INSPECTION_PERSONNEL_REPORT';
    if (registryType === 'Miscellaneous') return REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT;
    return '';
  }, [registryType]);

  // Process versions - group by report type with latest and older versions, filtered by registryType
  const processedVersions = useMemo(() => {
    // Use the appropriate data source based on registryType
    const dataSource =
      registryType === 'QC Inspection Personnel' ||
      registryType === 'Welder Certification & Qualification'
        ? inspectionPersonnelDocuments
        : registryReportVersions;
    
    if (!dataSource || typeof dataSource !== 'object') {
      return {};
    }
    
    const result: Record<string, { latest: any | null; older: any[]; total: number }> = {};
    
    Object.keys(dataSource).forEach((reportType) => {
      // Filter by registryType - for QC Inspection Personnel use exact match, others use prefix
      if (reportTypePrefix) {
        if (
          registryType === 'QC Inspection Personnel' ||
          registryType === 'Miscellaneous'
        ) {
          if (reportType !== reportTypePrefix) return;
        } else {
          // Prefix match for Equipment and Consumable
          if (!reportType.startsWith(reportTypePrefix)) return;
        }
      }
      
      const docs = dataSource[reportType] || [];
      if (docs.length === 0) return;
      
      // Sort by docRef descending (latest first)
      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });
      
      result[reportType] = {
        latest: sorted[0] || null,
        older: sorted.slice(1),
        total: sorted.length
      };
    });
    
    return result;
  }, [registryReportVersions, inspectionPersonnelDocuments, reportTypePrefix, registryType]);

  // Get total document count across all report types
  const totalDocumentsCount = useMemo(() => {
    return Object.values(processedVersions).reduce((sum, group) => sum + (group?.total || 0), 0);
  }, [processedVersions]);

  // Toggle group expansion
  const toggleGroupExpansion = (reportType: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportType)) {
        newSet.delete(reportType);
      } else {
        newSet.add(reportType);
      }
      return newSet;
    });
  };

  // Format report type for display (e.g., EQUIPMENT_WELDING_REPORT -> Equipment Welding Report)
  const formatReportType = (reportType: string): string => {
    // Special case for QC Personnel - display as "QC Personnel Register Reports"
    if (reportType === 'QC_INSPECTION_PERSONNEL_REPORT') {
      return 'QC Personnel Register Reports';
    }
    if (reportType === REPORT_TYPES.WELDER_CERTIFICATION_QUALIFICATION_REPORT) {
      return 'Welder Certification & Qualification Reports';
    }
    if (reportType === REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT) {
      return 'Miscellaneous Registry Reports';
    }
    return reportType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const filteredRegistryItems = useMemo(() => {
    return registryItems;
  }, [registryItems]);

  // Handle save draft - saves draft with consolidated report type
  const handleSaveDraft = useCallback(() => {
    if (!projectId) return;
    if (
      registryType !== 'QC Inspection Personnel' &&
      registryType !== 'Welder Certification & Qualification' &&
      registryType !== 'Consumable' &&
      registryType !== 'Miscellaneous' &&
      selectedTabs.length === 0
    )
      return;

    // Build signatures object
    const signatures: Record<string, any> = {
      qc: signatureData?.signature?.qc ? {
        name: signatureData.signature.qc.name || '',
        signature: signatureData.signature.qc.signature || '',
        stampImage: signatureData.signature.qc.stampImage || '',
        timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
        signed: true,
        comment: signatureData.signature.qc.comment || ''
      } : { signed: false },
      customer: signatureData?.signature?.customer ? {
        name: signatureData.signature.customer.name || '',
        signature: signatureData.signature.customer.signature || '',
        stampImage: signatureData.signature.customer.stampImage || '',
        timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
        signed: true,
        comment: signatureData.signature.customer.comment || ''
      } : { signed: false }
    };

    // Build form data
    const resolvedTabs = resolveTabsForActiveContext(
      registryType,
      activeTabName,
      registryItems,
      selectedTabs,
    );
    const formData = {
      selectedTabs: resolvedTabs,
      registryType,
      procedureDocumentNo: procedureDocumentNo.trim(),
      registerRevision: registerRevision.trim() || '0',
      selectedQcInspectorId: selectedQcInspectorId || null,
      selectedCustomerRepresentativeId: selectedCustomerRepresentativeId || null,
    };

    const draftReportType = getDraftReportType(registryType, activeTabName || resolvedTabs[0]);

    // Build save draft payload
    const saveDraftPayload: any = {
      projectId,
      reportType: draftReportType,
      formData,
      signatures
    };

    // Include draftId if it exists (for updating existing draft)
    if (draftId) {
      saveDraftPayload.draftId = draftId;
    }

    // Call save draft
    saveDraft(saveDraftPayload);

    setIsDirty(false);
  }, [
    projectId,
    selectedTabs,
    signatureData,
    registryType,
    activeTabName,
    procedureDocumentNo,
    registerRevision,
    saveDraft,
    draftId,
    selectedQcInspectorId,
    selectedCustomerRepresentativeId,
    registryItems,
  ]);

  // Handle generate report - generates individual reports per selected tab with specific report types
  const handleGenerateReport = useCallback(() => {
    if (!isFormValid) {
      // QC Inspection Personnel doesn't need selectedTabs
      if (
        registryType !== 'QC Inspection Personnel' &&
        registryType !== 'Welder Certification & Qualification' &&
        registryType !== 'Consumable' &&
        registryType !== 'Miscellaneous' &&
        selectedTabs.length === 0
      ) {
        const tabExamples = registryType === 'Equipment' ? 'Welding, NDE, QC' : 'Wire, Gas';
        Notification.error('No Registry Types Selected', `Please select at least one registry type (${tabExamples}) to generate reports.`);
        return;
      }
      if (!isBothSignaturesProvided) {
        Notification.error('Signatures Required', 'Please add both signatures (QC and Customer Representative) before generating reports.');
        return;
      }
      return;
    }

    // QC & Welder: single report with registerRevision (personnel-style payload)
    if (registryType === 'QC Inspection Personnel') {
      const payload = {
        projectId,
        reportType: REPORT_TYPES.QC_INSPECTION_PERSONNEL_REPORT,
        registerRevision: registerRevision.trim() || '0',
        registryIds: [],
        qcSignature: signatureData?.signature?.qc ? {
          name: signatureData.signature.qc.name || '',
          signature: signatureData.signature.qc.signature || '',
          stampImage: signatureData.signature.qc.stampImage || '',
          timestamp: signatureData.signature.qc.timestamp || new Date().toISOString()
        } : undefined,
        customerRepSignature: signatureData?.signature?.customer ? {
          name: signatureData.signature.customer.name || '',
          signature: signatureData.signature.customer.signature || '',
          stampImage: signatureData.signature.customer.stampImage || '',
          timestamp: signatureData.signature.customer.timestamp || new Date().toISOString()
        } : undefined,
        emailAddress: []
      };

      dispatch(generateMaterialRegistryReport(payload));
    } else if (registryType === 'Welder Certification & Qualification') {
      const payload = {
        projectId,
        reportType: REPORT_TYPES.WELDER_CERTIFICATION_QUALIFICATION_REPORT,
        registerRevision: registerRevision.trim() || '0',
        registryIds: [],
        qcSignature: signatureData?.signature?.qc
          ? {
              name: signatureData.signature.qc.name || '',
              signature: signatureData.signature.qc.signature || '',
              stampImage: signatureData.signature.qc.stampImage || '',
              timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
            }
          : undefined,
        customerRepSignature: signatureData?.signature?.customer
          ? {
              name: signatureData.signature.customer.name || '',
              signature: signatureData.signature.customer.signature || '',
              stampImage: signatureData.signature.customer.stampImage || '',
              timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
            }
          : undefined,
        emailAddress: [],
      };
      dispatch(generateMaterialRegistryReport(payload));
    } else if (registryType === 'Miscellaneous') {
      const payload = {
        projectId,
        reportType: REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT,
        registerRevision: registerRevision.trim() || '0',
        registryIds: [],
        qcSignature: signatureData?.signature?.qc
          ? {
              name: signatureData.signature.qc.name || '',
              signature: signatureData.signature.qc.signature || '',
              stampImage: signatureData.signature.qc.stampImage || '',
              timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
            }
          : undefined,
        customerRepSignature: signatureData?.signature?.customer
          ? {
              name: signatureData.signature.customer.name || '',
              signature: signatureData.signature.customer.signature || '',
              stampImage: signatureData.signature.customer.stampImage || '',
              timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
            }
          : undefined,
        emailAddress: [],
      };
      dispatch(generateMaterialRegistryReport(payload));
    } else {
      // Validate procedureDocumentNo for Equipment and Consumable
      if (!procedureDocumentNo.trim()) {
        Notification.error('Procedure Document No Required', 'Please enter a Procedure Document No to generate reports.');
        return;
      }
      
      // For Consumable/Equipment, honor the registry tab the user opened from
      let tabsToProcess = resolveTabsForActiveContext(
        registryType,
        activeTabName,
        registryItems,
        selectedTabs,
      );
      if (registryType === 'Consumable' && tabsToProcess.length === 0) {
        tabsToProcess = registryItems.length > 0
          ? registryItems.map((item) => item.tags_name)
          : ['NDE'];
      }
      
      const finalTabs = tabsToProcess;
      
      // Generate report for each selected tab
      finalTabs.forEach(tabName => {
        const reportType = getReportTypeForTab(registryType, tabName);
        
        // Get registry IDs for items matching this tab
        const registryIds = registryItems
          .filter(item => item.tags_name === tabName)
          .map(item => item.tags_id);

        // Build payload for API
        const payload = {
          projectId,
          reportType,
          procedureDocumentNo: procedureDocumentNo.trim(),
          registerRevision: registerRevision.trim() || '0',
          registryIds,
          qcSignature: signatureData?.signature?.qc ? {
            name: signatureData.signature.qc.name || '',
            signature: signatureData.signature.qc.signature || '',
            timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
            stampImage: signatureData.signature.qc.stampImage ?? ''
          } : undefined,
          customerRepSignature: signatureData?.signature?.customer ? {
            name: signatureData.signature.customer.name || '',
            signature: signatureData.signature.customer.signature || '',
            timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
            stampImage: signatureData.signature.customer.stampImage ?? ''
          } : undefined,
          emailAddress: []
        };
        // Dispatch Redux action
        dispatch(generateMaterialRegistryReport(payload));
      });
    }

    if (onRefresh) {
      onRefresh();
    }
  }, [isFormValid, selectedTabs, isBothSignaturesProvided, registryType, activeTabName, projectId, procedureDocumentNo, registerRevision, registryItems, signatureData, dispatch, onRefresh]);

  // Handle download/view report
  const handleDownloadReport = (doc: any) => {
    if (!doc || !(doc.fileUrl || doc.file_url)) {
      return;
    }
    setViewerFile({
      fileUrl: doc.fileUrl || doc.file_url,
      fileName: doc.fileName || doc.file_name || 'Equipment Report'
    });
  };

  // Close viewer
  const closeViewer = () => {
    setViewerFile(null);
    // Clear the report success state to prevent auto-reopening
    dispatch(clearMaterialRegistryReport());
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSignatureData(null);
      setSelectedCustomerRepresentativeId(null);
      setSelectedQcInspectorId(null);
      setSelectedTabs([]);
      setIsDirty(false);
      setHasSavedDraft(false);
      setViewerFile(null);
      setExpandedGroups(new Set());
      setProcedureDocumentNo('');
      setRegisterRevision('0');
      clearDraft();
      clearSaveStatus(); // Clear draft save success/error messages
      dispatch(clearMaterialRegistryReport());
    }
  }, [isOpen, clearDraft, clearSaveStatus, dispatch]);

  // Clear draft save status messages when modal opens (to clear any stale messages)
  useEffect(() => {
    if (isOpen) {
      clearSaveStatus();
    }
  }, [isOpen, clearSaveStatus]);

  // If viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        clearDraft();
        clearSaveStatus();
        onClose();
      }
    }}>
      <DialogContent
        className="max-w-[1280px] w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col bg-page-bg"
      >
          {/* Header */}
          <DialogHeader className="flex-shrink-0 pl-4 sm:pl-6 pr-14 py-4 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                {registryType === 'Consumable'
                  ? getConsumableReportTitle(activeTabName, selectedTabs)
                  : registryType === 'QC Inspection Personnel'
                  ? 'QC Inspection Personnel Registry Report'
                  : `${registryType} Registry Report`}
              </DialogTitle>
              <p className="text-[12px] text-heritage-grey mt-1 truncate">
                {projectName && `Project: ${projectName}`}
                {selectedTabs.length > 0 && (
                  <span className="ml-2">
                    | {selectedTabs.length} {registryType} Type(s) Selected
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || materialRegistryReportLoading || !isDraftValid || !isDirty}
                className="border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                title={!isDraftValid ? (registryType === 'Consumable' || registryType === 'QC Inspection Personnel' ? 'Fill in the required fields' : 'Select at least one registry type') : (!isDirty ? 'No changes to save' : '')}
              >
                {isSavingDraft ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={handleGenerateReport}
                disabled={!canGenerate || materialRegistryReportLoading || isSavingDraft}
                className="bg-hero-red hover:bg-hero-red-60 text-white"
                title={generateButtonTooltip}
              >
                {materialRegistryReportLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Report'
                )}
              </Button>
            </div>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 relative">
            {/* Blur overlay with loader */}
            {isFetchingDraft && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg">
                <Loader2 className="h-8 w-8 animate-spin text-hero-red mb-4" />
                <span className="text-[13px] text-heritage-grey">Loading draft data...</span>
              </div>
            )}
            {/* Form content - always visible, disabled during loading */}
            <div className={cn("space-y-8", isFetchingDraft && "pointer-events-none opacity-50")}>
                {/* Previous Reports / Version History - Grouped by Report Type */}
                
                {/* Registry Type Selection - Hidden for QC Inspection Personnel */}
                {registryType !== 'QC Inspection Personnel' &&
                  registryType !== 'Consumable' &&
                  registryType !== 'Welder Certification & Qualification' &&
                  registryType !== 'Miscellaneous' && (
                  <div className="flex flex-wrap items-center gap-2">
                    {filteredRegistryItems.map((item) => {
                      const isSelected = selectedTabs.includes(item.tags_name);
                      return (
                        <button
                          key={item.tags_id}
                          type="button"
                          onClick={() => handleTabToggle(item.tags_name)}
                          className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors",
                            isSelected
                              ? "bg-hero-red text-white hover:bg-hero-red-60"
                              : "bg-white text-text-grey border border-[#E2E5EA] hover:bg-page-bg"
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                          {item.tags_name}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Report Details - Different fields for QC Inspection Personnel */}
                {registryType === 'QC Inspection Personnel' || registryType === 'Miscellaneous' ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="registerRevision" className="text-[12px] font-medium text-text-grey">Register Rev <span className="text-hero-red">*</span></Label>
                        <Input
                          id="registerRevision"
                          value={registerRevision}
                          onChange={(e) => { setRegisterRevision(e.target.value); setIsDirty(true); }}
                          placeholder="e.g., 0"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="procedureDocumentNo" className="text-[12px] font-medium text-text-grey">Procedure Document No <span className="text-hero-red">*</span></Label>
                        <Input
                          id="procedureDocumentNo"
                          value={procedureDocumentNo}
                          onChange={(e) => { setProcedureDocumentNo(e.target.value); setIsDirty(true); }}
                          placeholder="e.g., 18.015.00.700.007"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="registerRevision" className="text-[12px] font-medium text-text-grey">Register Revision</Label>
                        <Input
                          id="registerRevision"
                          value={registerRevision}
                          onChange={(e) => { setRegisterRevision(e.target.value); setIsDirty(true); }}
                          placeholder="e.g., 0"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Signatures */}
                <div className="space-y-4 [&>div:first-child]:border-t-0 [&>div:first-child]:pt-0">
                  <Signature
                    customerSignature={selectedCustomerRepresentativeId}
                    qcSignature={selectedQcInspectorId}
                    customerRepresentativeList={customerRepresentativeList}
                    qcList={qcList}
                    handleQcSignatureChange={handleQcSignatureChange}
                    handleCustomerSignatureChange={handleCustomerSignatureChange}
                    signatureData={signatureData}
                    onSignatureChange={OnHandleSignature}
                    canSign={canSign}
                    canEdit={canEdit}
                    isEditMode={isProductionSheetEditMode}
                    isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                    isQCInspectorUser={isQCInspectorUser}
                    isWelderUser={isWelderUser}
                    currentUserTeamMemberId={currentUserTeamMemberId}
                    isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                  />

                  {/* Information message about required signatures */}
                  {!isBothSignaturesProvided && (
                    <div className="flex items-start gap-2 p-3 bg-[rgba(0,39,63,0.04)] border border-[rgba(0,39,63,0.15)] rounded-lg">
                      <Info className="h-5 w-5 text-blueprint-blue flex-shrink-0 mt-0.5" />
                      <div className="text-[13px] text-blueprint-blue">
                        <div className="font-medium mb-1">Required Signatures to Generate Report:</div>
                        <ul className="list-disc list-inside space-y-1">
                          {!signatureData?.signature?.qc && (
                            <li>QC Inspector signature is required</li>
                          )}
                          {!signatureData?.signature?.customer && (
                            <li>Customer Representative signature is required</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Success Message */}
                {materialRegistryReportSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(16,136,92,0.06)] border border-[rgba(16,136,92,0.2)] rounded-lg">
                    <CheckCircle className="h-5 w-5 text-[#10885C]" />
                    <div className="text-[13px] text-[#10885C]">
                      <span>{registryType} registry reports generated successfully!</span>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {materialRegistryReportError && (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
                    <AlertCircle className="h-5 w-5 text-hero-red" />
                    <div className="text-[13px] text-hero-red">
                      <div className="font-medium">Error: {materialRegistryReportError}</div>
                    </div>
                  </div>
                )}

                {/* Draft Status */}
                {saveDraftSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(0,39,63,0.04)] border border-[rgba(0,39,63,0.15)] rounded-lg">
                    <CheckCircle className="h-5 w-5 text-blueprint-blue" />
                    <div className="text-[13px] text-blueprint-blue">
                      <span>Draft saved successfully!</span>
                    </div>
                  </div>
                )}

                {saveDraftError && (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
                    <AlertCircle className="h-5 w-5 text-hero-red" />
                    <div className="text-[13px] text-hero-red">
                      <span>Failed to save draft: {saveDraftError}</span>
                    </div>
                  </div>
                )}

              </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};
